import React from "react";
import { useTranslation } from "react-i18next";

type Stats = {
  total_records: number;
  duplicates_skipped: number;
  skipped_due_to_capacity: number;
  jobs_created_count: number;
  queue_name: string;
  queue_threshold: number;
  current_queue_count: number;
  capacity_available: number;
  queue_over_threshold: boolean;
  is_test: boolean;
  http_status_code?: number;
  api_response?: string;
};

/**
 * Renders a localized summary view for a data pull/integration operation, including
 * an optional message, key performance statistics, and a list of created job IDs.
 **/
export default function Summary({
  result,
  title = "Result Summary",
}: {
  result?;
  title?: string;
}) {
  const { t } = useTranslation();

  if (!result) return null;

  const payload = result?.summary || result?.data?.summary || {};
  const message: string = result?.message || result?.data?.message || "";
  const stats: Partial<Stats> | undefined = payload?.stats;
  const jobs: string[] = payload?.jobs || [];
  const ordersToDelete: string[] =
    result?.ordersToDelete || result?.data?.ordersToDelete || [];
  const errors: string[] = result?.errors || result?.data?.errors || [];
  // Helper to translate with default value
  const tr = (key: string, dv: string) => t(key, { defaultValue: dv });

  return (
    <div>
      {message && <p className="mb-3 font-semibold text-gray-900">{message}</p>}

      {stats && (
        <>
          <h5 className="mt-0 mb-2 text-sm font-semibold text-gray-900">
            {title}
          </h5>

          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <div className="rounded border border-gray-200 p-3">
              <dt className="text-xs font-medium text-gray-500">
                {tr("translation:total_records", "Total Records")}
              </dt>
              <dd className="mt-1 text-sm font-semibold text-gray-900">
                {stats.total_records ?? 0}
              </dd>
            </div>

            <div className="rounded border border-gray-200 p-3">
              <dt className="text-xs font-medium text-gray-500">
                {tr("translation:jobs_created", "Jobs Created")}
              </dt>
              <dd className="mt-1">
                <span
                  className={[
                    "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium",
                    (stats.jobs_created_count ?? 0) > 0
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800",
                  ].join(" ")}
                >
                  {stats.jobs_created_count ?? 0}
                </span>
              </dd>
            </div>

            <div className="rounded border border-gray-200 p-3">
              <dt className="text-xs font-medium text-gray-500">
                {tr("translation:duplicates_skipped", "Duplicates Skipped")}
              </dt>
              <dd className="mt-1 text-sm font-semibold text-gray-900">
                {stats.duplicates_skipped ?? 0}
              </dd>
            </div>

            <div className="rounded border border-gray-200 p-3">
              <dt className="text-xs font-medium text-gray-500">
                {tr("translation:skipped_capacity", "Skipped (Capacity)")}
              </dt>
              <dd className="mt-1 text-sm font-semibold text-gray-900">
                {stats.skipped_due_to_capacity ?? 0}
              </dd>
            </div>

            <div className="rounded border border-gray-200 p-3">
              <dt className="text-xs font-medium text-gray-500">
                {tr("translation:queue_threshold", "Queue Threshold")}
              </dt>
              <dd className="mt-1 text-sm font-semibold text-gray-900">
                {stats.queue_threshold ?? 0}
              </dd>
            </div>

            <div className="rounded border border-gray-200 p-3">
              <dt className="text-xs font-medium text-gray-500">
                {tr("translation:current_queue_count", "Current Queue Count")}
              </dt>
              <dd className="mt-1 text-sm font-semibold text-gray-900">
                {stats.current_queue_count ?? 0}
              </dd>
            </div>

            <div className="rounded border border-gray-200 p-3">
              <dt className="text-xs font-medium text-gray-500">
                {tr("translation:over_threshold", "Over Threshold?")}
              </dt>
              <dd className="mt-1">
                <span
                  className={[
                    "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium",
                    stats.queue_over_threshold
                      ? "bg-red-100 text-red-800"
                      : "bg-blue-100 text-blue-800",
                  ].join(" ")}
                >
                  {stats.queue_over_threshold
                    ? t("translation:yes")
                    : t("translation:no")}
                </span>
              </dd>
            </div>

            <div className="rounded border border-gray-200 p-3">
              <dt className="text-xs font-medium text-gray-500">
                {tr("translation:test_mode", "Test Mode")}
              </dt>
              <dd className="mt-1 text-sm font-semibold text-gray-900">
                {stats.is_test ? t("translation:yes") : t("translation:no")}
              </dd>
            </div>

            {stats.http_status_code !== undefined &&
              stats.http_status_code !== null && (
                <div className="rounded border border-gray-200 p-3">
                  <dt className="text-xs font-medium text-gray-500">
                    {tr("translation:http_status_code", "HTTP Status Code")}
                  </dt>
                  <dd className="mt-1">
                    <span
                      className={[
                        "inline-flex items-center rounded px-2 py-0.5 text-xs font-medium",
                        stats.http_status_code >= 200 &&
                        stats.http_status_code < 300
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800",
                      ].join(" ")}
                    >
                      {stats.http_status_code}
                    </span>
                  </dd>
                </div>
              )}
          </div>

          {stats.api_response && (
            <div className="mt-4 rounded border border-gray-200 p-3">
              <dt className="text-xs font-medium text-gray-500 mb-2">
                {tr("translation:api_response", "API Response")}
              </dt>
              <dd className="mt-1">
                <pre className="text-xs text-gray-900 whitespace-pre-wrap break-words max-h-64 overflow-y-auto bg-gray-50 p-2 rounded">
                  {stats.api_response}
                </pre>
              </dd>
            </div>
          )}

          {/* Prefer job-style summary; omit lines-deleted block */}
        </>
      )}

      {/* Only show orders list when no jobs were created (parity with external tests) */}
      {ordersToDelete &&
        ordersToDelete.length > 0 &&
        (stats?.jobs_created_count ?? 0) === 0 && (
          <>
            <hr className="my-4 border-t border-gray-200" />
            <h5 className="mb-2 text-sm font-semibold text-gray-900">
              {tr("translation:orders_to_delete", "Orders to Delete")} (
              {ordersToDelete.length})
            </h5>
            <ul className="divide-y rounded border border-gray-200">
              {ordersToDelete.map((item) => (
                <li key={item} className="p-2 text-sm">
                  {item}
                </li>
              ))}
            </ul>
          </>
        )}

      {errors && errors.length > 0 && (
        <>
          <hr className="my-4 border-t border-gray-200" />
          <h5 className="mb-2 text-sm font-semibold text-red-700">
            {tr("translation:errors", "Errors")} ({errors.length})
          </h5>
          <ul className="divide-y rounded border border-red-200 bg-red-50">
            {errors.map((error) => (
              <li key={error} className="p-2 text-sm text-red-800">
                {error}
              </li>
            ))}
          </ul>
        </>
      )}

      {jobs && jobs.length > 0 && (
        <>
          <hr className="my-4 border-t border-gray-200" />
          <h5 className="mb-2 text-sm font-semibold text-gray-900">
            {t("translation:jobs")} ({jobs.length})
          </h5>
          <ul className="divide-y rounded border border-gray-200">
            {jobs.map((item) => (
              <li key={item} className="p-2 text-sm">
                {item}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
